import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  fetchSignInMethodsForEmail,
  EmailAuthProvider,
  linkWithCredential,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  AuthProvider,
} from 'firebase/auth';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyInvite } from '@core/models/company-invite';
import { User } from '@core/models/user';
import { AuthService } from '@core/services/auth.service';
import { CompanyInviteService } from '@core/services/company-invite.service';
import { UserService } from '@core/services/user.service';
import { PaymentService } from '@core/services/payment.service';
import { FirebaseInjectionHelperService } from '@core/services/firebase-injection-helper.service';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SignupComponent {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private inviteService = inject(CompanyInviteService);
  private router = inject(Router);
  private paymentService = inject(PaymentService);
  private firebaseHelper = inject(FirebaseInjectionHelperService);

  form: FormGroup;
  type = 'signup';
  loading = false;
  serverMessage: string | undefined;
  existingProviders: string[] = [];
  showMergePrompt = false;
  inviteCode?: string;
  emailDisabled: boolean = false;

  constructor() {
    this.form = this.fb.group({
      email: [
        { value: '', disabled: this.emailDisabled },
        [Validators.required, Validators.email],
      ],
      password: [
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(15), // Enhanced security policy requirement (Firebase minimum is 6)
        ]),
      ],
      passwordConfirm: ['', [Validators.required]],
    });

    this.checkInviteCode();
  }

  private checkInviteCode() {
    this.route.queryParams.subscribe((params) => {
      const inviteCode = params['inviteCode'];
      if (inviteCode) {
        // Redeem the invite code
        this.inviteService
          .validateInviteCode(inviteCode)
          .then((res) => {
            if (res) {
              this.inviteService
                .getDocument(inviteCode)
                .then((inviteCode: CompanyInvite) => {
                  if (inviteCode) {
                    this.emailDisabled = true;
                    this.form.get('email')?.setValue(inviteCode.email);
                    this.form.get('email')?.disable();
                  }
                });
            }
          })
          .catch((error: any) => {
            // Handle redemption error
            this.serverMessage =
              'Error redeeming invite code: ' + error.message;
          });
      }
    });
  }

  get email() {
    return this.form.get('email');
  }
  get password() {
    return this.form.get('password');
  }

  get passwordConfirm() {
    return this.form.get('passwordConfirm');
  }

  /**
   * Whether a control should surface its validation error yet. Errors appear
   * once the user has edited the field (dirty) or moved away from it (touched),
   * so a field the user has not reached yet stays quiet.
   *
   * @param fieldName - Name of the control on the signup form.
   * @returns True when the control is invalid and has been interacted with.
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  /**
   * True once the confirmation field has been interacted with and holds a value
   * that does not match the password. The empty case is reported as a required
   * error instead, so the two messages never overlap.
   */
  get showPasswordMismatch(): boolean {
    const confirm = this.passwordConfirm;
    if (!confirm || !(confirm.dirty || confirm.touched)) {
      return false;
    }
    return !confirm.hasError('required') && !this.passwordDoesMatch;
  }

  get passwordDoesMatch() {
    if (this.type !== 'signup') {
      return true;
    } else {
      if (this.password && this.passwordConfirm) {
        return this.password.value === this.passwordConfirm.value;
      } else {
        return false;
      }
    }
  }

  get passwordValid() {
    return this.form.controls['password'].errors === null;
  }

  get requiredValid() {
    return !this.form.controls['password'].hasError('required');
  }

  get minLengthValid() {
    const passwordControl = this.form.controls['password'];
    return (
      passwordControl.value &&
      passwordControl.value.length >= 15 &&
      !passwordControl.hasError('minlength')
    );
  }

  /**
   * Handles the submission of email and password for signup. It prevents signups
   * using public email providers and checks if an account with the provided email
   * already exists.
   *
   * @returns A boolean indicating the success or failure of the signup operation.
   */
  async onSubmit() {
    this.loading = true;

    const email = this.email ? this.email.value : null;
    const password = this.password ? this.password.value : null;

    // Validate email and password input
    if (!email || !password) {
      this.serverMessage = 'Enter Email and Password';
      this.loading = false;
      return false;
    }

    try {
      // Attempt to create a new user with the provided email and password
      const userCred = await this.authService.registerWithEmailPassword(
        email,
        password,
      );
      // If successful, proceed with setting the user model and navigating
      this.userService.setUserModel(new User(userCred.user));

      // Send Firebase email verification so the user can confirm their address.
      // This is non-blocking – the signup flow continues regardless of whether
      // the verification email is sent successfully.
      this.userService.sendVerificationEmail(userCred.user);

      // Create Stripe customer for the new user (non-blocking)
      // This runs in background and won't block the signup flow
      this.paymentService.createOrGetStripeCustomer().catch((err: any) => {
        console.warn('Failed to create Stripe customer during signup:', err);
      });

      // Redirect to profile completion first, then company profile
      // Carry invite code so profile-completion can redeem it after saving the profile
      const navExtras = this.inviteCode
        ? { queryParams: { inviteCode: this.inviteCode } }
        : {};
      await this.router.navigate(['/register/profile-completion'], navExtras);
    } catch (err: any) {
      // Detect email already exists from both the Firebase SDK code and the raw
      // Identity Platform REST response. The SDK wraps the 400/EMAIL_EXISTS response
      // as auth/network-request-failed, burying the real error in customData.message.
      const isEmailExists =
        err.code === 'auth/email-already-in-use' ||
        err.message?.includes('EMAIL_EXISTS') ||
        err.customData?.message?.includes('email-already-in-use');

      if (isEmailExists) {
        await this.handleExistingEmail(email);
      } else {
        this.serverMessage = this.getSignupErrorMessage(err);
      }
      this.loading = false;
      return false;
    }

    this.loading = false;
    return true;
  }

  /**
   * Maps Firebase Auth client SDK error codes to actionable user-facing messages.
   * Covers all error codes that can be returned during email/password account creation.
   */
  private getSignupErrorMessage(err: any): string {
    switch (err.code) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Use "Sign In" or try a different email.';
      case 'auth/invalid-email':
        return 'The email address is not valid. Please check and try again.';
      case 'auth/operation-not-allowed':
        return 'Email/password sign-up is not enabled. Please contact support.';
      case 'auth/weak-password':
        return 'Your password is too weak. Please choose a stronger password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth/network-request-failed':
        return 'A network error occurred. Please check your connection and try again.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/invalid-credential':
        return 'The provided credentials are invalid. Please try again.';
      case 'auth/admin-restricted-operation':
        return 'Self-service registration is not available. Please contact your administrator.';
      case 'auth/missing-email':
        return 'Please enter an email address.';
      case 'auth/missing-password':
        return 'Please enter a password.';
      default:
        return 'An unexpected error occurred during sign-up. Please try again.';
    }
  }

  /**
   * Fetches existing sign-in methods for the email and activates the merge prompt.
   */
private async handleExistingEmail(email: string): Promise<void> {
    try {
      const auth = this.firebaseHelper.getAuth();
      this.existingProviders = await fetchSignInMethodsForEmail(auth, email);
    } catch {
      this.existingProviders = [];
    }
    this.showMergePrompt = true;
    this.serverMessage = undefined;
  }

  /** Clears the merge prompt so the user can try a different email address. */
  dismissMergePrompt(): void {
    this.showMergePrompt = false;
    this.existingProviders = [];
  }

  /** Navigates to the login page. */
  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  /**
   * Signs the user in via an existing OAuth provider (Google or Microsoft) and,
   * if the user supplied a password on this form, links that email/password
   * credential to the account so they can also sign in with password in future.
   *
   * @param providerName - Firebase provider ID, e.g. 'google.com' or 'microsoft.com'
   */
  async mergeWithProvider(providerName: string): Promise<void> {
    this.loading = true;
    const email = this.email?.value as string;
    const password = this.password?.value as string;

    try {
      const auth = this.firebaseHelper.getAuth();
      let provider: AuthProvider;

      if (providerName === 'google.com') {
        const gProvider = new GoogleAuthProvider();
        gProvider.setCustomParameters({ login_hint: email });
        provider = gProvider;
      } else {
        const msProvider = new OAuthProvider('microsoft.com');
        msProvider.setCustomParameters({ login_hint: email });
        provider = msProvider;
      }

      const userCred = await signInWithPopup(auth, provider);

      // Attempt to link the email/password credential they entered on the form.
      // This is best-effort — if it fails (e.g. already linked) we still continue.
      if (email && password) {
        try {
          const emailCredential = EmailAuthProvider.credential(email, password);
          await linkWithCredential(userCred.user, emailCredential);
        } catch {
          // Credential already linked or incompatible — safe to ignore here.
        }
      }

      this.userService.setUserModel(new User(userCred.user));

      const navExtras = this.inviteCode
        ? { queryParams: { inviteCode: this.inviteCode } }
        : {};
      await this.router.navigate(['/register/profile-completion'], navExtras);
    } catch (_err: unknown) {
      this.serverMessage =
        'Sign-in failed. Please try again or use the login page.';
      this.showMergePrompt = false;
    } finally {
      this.loading = false;
    }
  }
}
